import React from "react";
import { ImportSteps, ValidationRow, SummaryStat } from "../../../../shared/components/ImportSteps";

interface ImportStepsCustomerProps {
    currentStep: "file" | "validation" | "confirmation";
    selectedFile: File | null;
    importMode: "flush" | "update" | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onImportModeChange: (mode: "flush" | "update") => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    validatedRows: ValidationRow[];
    onBack: () => void;
    onImport: () => void;
    originalRowCount?: number;
}

export const ImportStepsCustomer = (props: ImportStepsCustomerProps) => {
    const { originalRowCount, validatedRows, ...restProps } = props;
    
    // Calculate custom summary stats for customers (accounting for deduplication)
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    const uniqueCustomersCount = validatedRows.length;
    const totalRowsProcessed = originalRowCount || validatedRows.length;
    const duplicatesCount = totalRowsProcessed - uniqueCustomersCount;
    
    // Count all errors (including warnings from duplicates)
    const totalErrorsCount = validatedRows.reduce((sum, row) => sum + (row.errors?.length || 0), 0);
    
    const summaryStats: SummaryStat[] = [
        {
            value: uniqueCustomersCount,
            label: "Khách hàng",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: totalErrorsCount,
            label: "Lỗi",
            backgroundColor: totalErrorsCount > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: totalErrorsCount > 0 ? "#f44336" : "#4caf50",
            textColor: totalErrorsCount > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: duplicatesCount,
            label: "Dòng đã gộp",
            backgroundColor: duplicatesCount > 0 ? "#fff3e0" : "#e8f5e9",
            borderColor: duplicatesCount > 0 ? "#ff9800" : "#4caf50",
            textColor: duplicatesCount > 0 ? "#e65100" : "#2e7d32",
        },
        {
            value: totalRowsProcessed,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const fileFormatInstructions = (
        <>
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>Lưu ý về định dạng file:</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Headers phải ở dòng 3</li>
                <li>Dữ liệu bắt đầu từ dòng 4</li>
                <li>Trường bắt buộc: name (Tên khách hàng)</li>
                <li>
                    Các trường tùy chọn: customer_code, phone, tax_id, tier, email, address, note, category,
                    payable_days, credit_limit, is_internal
                </li>
                <li>
                    Để nhập thông tin liên hệ, sử dụng tiền tố: contact_list.name, contact_list.email,
                    contact_list.phone, contact_list.position
                </li>
            </ul>
        </>
    );

    return (
        <ImportSteps
            {...restProps}
            validatedRows={validatedRows}
            title="Nhập danh sách khách hàng"
            summaryStats={summaryStats}
            templateUrl="/templates/customer_import.xlsx"
            importModeLabels={{
                update: {
                    title: "Cập nhật và thêm mới",
                    description: "Khách hàng mới sẽ được thêm vào. Khách hàng trùng mã sẽ được cập nhật với dữ liệu từ file.",
                },
                flush: {
                    title: "Xóa tất cả và nhập mới",
                    description: "Tất cả khách hàng hiện có sẽ bị xóa vĩnh viễn và thay thế bằng dữ liệu từ file. Hành động này không thể hoàn tác.",
                    warning: "CẢNH BÁO",
                },
            }}
            fileFormatInstructions={fileFormatInstructions}
            previewColumns={[
                { key: "name", label: "Tên KH" },
                { key: "customer_code", label: "Mã KH" },
                { key: "phone", label: "SĐT" },
                { key: "email", label: "Email" },
                { key: "tax_id", label: "Mã số thuế" },
                { key: "tier", label: "Cấp độ" },
                { key: "category", label: "Danh mục" },
                { key: "address", label: "Địa chỉ" },
                { key: "payable_days", label: "Số ngày nợ" },
                { key: "credit_limit", label: "Hạn mức" },
                { key: "is_internal", label: "Nội bộ" },
                { key: "contact_list", label: "Người liên hệ", isNested: true },
            ]}
        />
    );
};

